// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AdminPanelSettingsOutlined,
  Download as DownloadIcon,
} from '@/constants/hardfin-standard-icons';
import type {
  CsvImportColumnSelection,
  CsvImportTemplateColumn,
} from '@/csv-import/types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { ReactNode, useCallback } from 'react';

interface CsvImportTemplateStepProps {
  title: string;
  /** Columns in template order; the caller applies any permission filtering */
  columns: CsvImportTemplateColumn[];
  /** Rule shared by the columns, stated once instead of repeated per row */
  columnsHint?: string;
  /** Owned by the flow so the selection survives step navigation */
  selectedColumns: CsvImportColumnSelection;
  onToggleColumn: (header: string) => void;
  infoAlert?: ReactNode;
  fileNamePrefix: string;
  onBack: () => void;
  onNext: () => void;
}

function isColumnIncluded(
  column: CsvImportTemplateColumn,
  selectedColumns: CsvImportColumnSelection
): boolean {
  return column.alwaysIncluded || (selectedColumns[column.header] ?? true);
}

/** Headers written for a column: itself, plus any children it carries along */
function templateHeaders(column: CsvImportTemplateColumn): string[] {
  return [
    column.header,
    ...(column.children ?? []).map((child) => child.header),
  ];
}

// The required column is the one the picker will not let you clear
const SECTIONS = [
  {
    title: 'Required',
    matches: (column: CsvImportTemplateColumn) =>
      column.alwaysIncluded === true,
  },
  {
    title: 'Optional',
    matches: (column: CsvImportTemplateColumn) =>
      column.alwaysIncluded !== true,
  },
];

// Keeps every column name in one scannable column beside the descriptions
const HEADER_COLUMN_WIDTH = 17;
// Lane a checkbox and its gap occupy ahead of a parent column name
const CHECKBOX_LANE_WIDTH = 3.5;
// Margin, padding, and rule ahead of a child column name
const CHILD_INDENT_WIDTH = 4.75;
// Narrower name column so a child description lands in the same place as a parent one
const CHILD_HEADER_COLUMN_WIDTH =
  HEADER_COLUMN_WIDTH + CHECKBOX_LANE_WIDTH - CHILD_INDENT_WIDTH;

interface ColumnDetailsProps {
  column: CsvImportTemplateColumn;
  /** Parent column a child rides along with, shown once beside the child */
  pairedWith?: string;
}

function ColumnDetails(props: ColumnDetailsProps) {
  const headerWidth =
    props.pairedWith === undefined
      ? HEADER_COLUMN_WIDTH
      : CHILD_HEADER_COLUMN_WIDTH;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        columnGap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: (theme) => theme.spacing(headerWidth),
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {props.column.header}
        </Typography>
        {props.column.restricted && (
          <Tooltip
            arrow
            title="Permission protected, so not everyone can set this"
          >
            <AdminPanelSettingsOutlined fontSize="small" color="action" />
          </Tooltip>
        )}
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flex: 1, minWidth: (theme) => theme.spacing(30) }}
      >
        {props.column.description}
        {props.column.blankNote !== undefined && (
          <Typography
            component="span"
            variant="body2"
            color="warning.dark"
            sx={{ ml: 1 }}
          >
            {props.column.blankNote}
          </Typography>
        )}
        {props.pairedWith !== undefined && (
          <Typography
            component="span"
            variant="body2"
            color="text.disabled"
            sx={{ ml: 1 }}
          >
            Included with {props.pairedWith.toLowerCase()}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

function escapeCsvField(field: string): string {
  if (!/[",\n]/.test(field)) {
    return field;
  }
  return `"${field.replaceAll('"', '""')}"`;
}

export function CsvImportTemplateStep(props: CsvImportTemplateStepProps) {
  const handleDownloadTemplate = useCallback(() => {
    const headers = props.columns
      .filter((column) => isColumnIncluded(column, props.selectedColumns))
      .flatMap(templateHeaders)
      .map(escapeCsvField);
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${props.fileNamePrefix}_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [props.columns, props.fileNamePrefix, props.selectedColumns]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            {props.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the columns to include in the template
          </Typography>
          {props.columnsHint !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {props.columnsHint}
            </Typography>
          )}
          {SECTIONS.map((section) => (
            <Box key={section.title} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {section.title}
              </Typography>
              <FormGroup>
                {props.columns
                  .filter((column) => section.matches(column))
                  .map((column) => (
                    <Box key={column.header} sx={{ py: 0.25 }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                        control={
                          <Checkbox
                            checked={isColumnIncluded(
                              column,
                              props.selectedColumns
                            )}
                            disabled={column.alwaysIncluded}
                            onChange={() => props.onToggleColumn(column.header)}
                            size="small"
                            sx={{ p: 0 }}
                          />
                        }
                        label={<ColumnDetails column={column} />}
                      />
                      {/* Children ride along with the parent, so a rule marks them
                          as part of it rather than as separate choices */}
                      {column.children !== undefined && (
                        <Box
                          sx={{
                            mt: 0.25,
                            ml: 3,
                            pl: 1.5,
                            borderLeft: (theme) =>
                              `2px solid ${theme.palette.divider}`,
                          }}
                        >
                          {column.children.map((child) => (
                            <ColumnDetails
                              key={child.header}
                              column={child}
                              pairedWith={column.header}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
              </FormGroup>
            </Box>
          ))}
        </Box>
        {props.infoAlert}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
